import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '@/lib/api'
import { Card, CardContent } from '@/components/ui/card'
import { PageHeader } from '@/components/dashboard-ui'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useToast } from '@/hooks/use-toast'
import { useImpersonation } from '@/hooks/useImpersonation'
import { Plus, Loader2, Eye, MessageSquare } from 'lucide-react'
import WidgetPreview from '@/components/WidgetPreview'

interface ClientRow {
  id: string
  name: string
  slug: string
  status: string
  subscriptionTier: string
  subscriptionStatus: string
  isDemo: boolean
  createdAt: string
}

const slugify = (name: string) =>
  name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60)

export default function FranchiseClientsPage() {
  const { toast } = useToast()
  const navigate = useNavigate()
  const { startImpersonation } = useImpersonation()
  const [rows, setRows] = useState<ClientRow[]>([])
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)

  const [open, setOpen] = useState(false)
  const [name, setName] = useState('')
  const [slug, setSlug] = useState('')
  const [slugTouched, setSlugTouched] = useState(false)
  const [ownerName, setOwnerName] = useState('')
  const [ownerEmail, setOwnerEmail] = useState('')
  const [ownerPassword, setOwnerPassword] = useState('')
  const [isDemo, setIsDemo] = useState(false)

  // Widget preview modal — shows the franchise-branded widget on a mock
  // client site. The widget self-resolves /api/chat/widget-config/{clientId},
  // which now inherits this franchise's brand for the client.
  const [previewClient, setPreviewClient] = useState<ClientRow | null>(null)
  const [previewConfig, setPreviewConfig] = useState<{
    clientId: string
    widgetKey?: string
    position: string
    theme: string
    primaryColor: string
    autoOpen: boolean
    showOnMobile: boolean
  } | null>(null)
  const [previewLoading, setPreviewLoading] = useState(false)

  const openPreview = async (row: ClientRow) => {
    setPreviewClient(row)
    setPreviewConfig(null)
    setPreviewLoading(true)
    try {
      const res = await api.get(`/api/chat/widget-config/${row.id}`)
      const c = res.data.config || {}
      setPreviewConfig({
        clientId: row.id,
        widgetKey: c.widgetKey || '',
        position: c.position || 'bottom-right',
        theme: c.theme || 'light',
        primaryColor: c.primaryColor || '#4d71fd',
        autoOpen: true,
        showOnMobile: true,
      })
    } catch {
      // Widget self-resolves config too; seed a minimal one so it still renders.
      setPreviewConfig({
        clientId: row.id,
        position: 'bottom-right',
        theme: 'light',
        primaryColor: '#4d71fd',
        autoOpen: true,
        showOnMobile: true,
      })
    } finally {
      setPreviewLoading(false)
    }
  }

  const load = useCallback(async () => {
    try {
      const res = await api.get('/api/franchise/clients')
      setRows(res.data.clients || [])
    } catch {
      toast({ title: 'Could not load clients', variant: 'destructive' })
    } finally {
      setLoading(false)
    }
  }, [toast])

  useEffect(() => { load() }, [load])

  const createClient = async () => {
    setBusy(true)
    try {
      await api.post('/api/franchise/clients', {
        name: name.trim(),
        slug: slug.trim(),
        ownerName: ownerName.trim(),
        ownerEmail: ownerEmail.trim(),
        ownerPassword,
        isDemo
      })
      toast({
        title: isDemo ? 'Demo client created' : 'Client created',
        description: `${name.trim()} is live. Their team can sign in with the owner account.`
      })
      setOpen(false)
      setName(''); setSlug(''); setSlugTouched(false)
      setOwnerName(''); setOwnerEmail(''); setOwnerPassword(''); setIsDemo(false)
      load()
    } catch (err: any) {
      toast({
        title: 'Could not create client',
        description: err?.response?.data?.message || 'Check the fields and try again.',
        variant: 'destructive'
      })
    } finally {
      setBusy(false)
    }
  }

  const viewAs = (row: ClientRow) => {
    startImpersonation(row.id, row.name, row.slug, row.subscriptionTier, true)
    navigate('/')
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Clients"
        subtitle="The businesses on your platform. Each gets their own dashboard, AI receptionist, and team."
        actions={
          <Button onClick={() => setOpen(true)}>
            <Plus className="h-4 w-4 mr-2" /> Add Client
          </Button>
        }
      />

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
          ) : rows.length === 0 ? (
            <div className="py-16 text-center text-sm text-muted-foreground">
              No clients yet. Add your first one — or create a demo client to sell with.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Business</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead>Added</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map(row => (
                  <TableRow key={row.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{row.name}</span>
                        {row.isDemo && <Badge variant="secondary">Demo</Badge>}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={row.status === 'suspended' ? 'destructive' : 'default'}>
                        {row.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground capitalize">{row.subscriptionTier}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {new Date(row.createdAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button variant="outline" size="sm" onClick={() => openPreview(row)}>
                          <MessageSquare className="h-4 w-4 mr-1" /> Preview widget
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => viewAs(row)}>
                          <Eye className="h-4 w-4 mr-1" /> View as
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Client</DialogTitle>
            <DialogDescription>
              Creates the business on your platform with its owner login. They inherit your branding automatically.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="cl-name">Business name</Label>
                <Input id="cl-name" value={name} placeholder="Smith Roofing"
                  onChange={e => {
                    setName(e.target.value)
                    if (!slugTouched) setSlug(slugify(e.target.value))
                  }} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cl-slug">Identifier</Label>
                <Input id="cl-slug" value={slug} className="font-mono"
                  onChange={e => { setSlugTouched(true); setSlug(slugify(e.target.value)) }} />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="cl-owner">Owner name</Label>
              <Input id="cl-owner" value={ownerName} placeholder="Jane Smith" onChange={e => setOwnerName(e.target.value)} />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="cl-email">Owner email</Label>
                <Input id="cl-email" type="email" value={ownerEmail} placeholder="jane@smithroofing.com" onChange={e => setOwnerEmail(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cl-pass">Temporary password</Label>
                <Input id="cl-pass" type="text" value={ownerPassword} placeholder="min 8 characters" onChange={e => setOwnerPassword(e.target.value)} />
              </div>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-border p-3">
              <div>
                <p className="text-sm font-medium">Demo client</p>
                <p className="text-xs text-muted-foreground">A free sandbox for sales demos — not billed, limited usage. Max 2.</p>
              </div>
              <Switch checked={isDemo} onCheckedChange={setIsDemo} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button
              onClick={createClient}
              disabled={busy || !name.trim() || slug.length < 3 || !ownerName.trim() || !ownerEmail.includes('@') || ownerPassword.length < 8}
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {isDemo ? 'Create Demo' : 'Add Client'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Widget preview — the franchise-branded chat widget on a mock client site */}
      <Dialog
        open={!!previewClient}
        onOpenChange={(o) => { if (!o) { setPreviewClient(null); setPreviewConfig(null) } }}
      >
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Widget preview — {previewClient?.name}</DialogTitle>
            <DialogDescription>
              How the chat widget looks on {previewClient?.name}'s site, wearing your platform brand.
              Clients inherit your colors and logo automatically and can override them later.
            </DialogDescription>
          </DialogHeader>
          {previewLoading || !previewConfig ? (
            <div className="flex justify-center py-24">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <WidgetPreview config={previewConfig} organizationName={previewClient?.name} />
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
